import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { DispatchCard } from '../components/dispatch-card.js';
import { JobFiles } from '../components/job-files.js';
import { JobNotes } from '../components/job-notes.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import {
  ApiError,
  api,
  type SalesOrder,
  type SalesOrderLineItem,
  type SalesOrderTax,
  type UpdateSalesOrderBody,
} from '../lib/api.js';
import { activeSalesTypes, orderTrade, useSalesTypes } from '../lib/trade.js';
import { useFieldJobsSettings } from '../lib/settings.js';
import { TradePill } from '../components/ui/trade-pill.js';

export function SalesOrdersDetailScreen() {
  const { id } = useParams({ from: '/sales-orders/$id' });
  const spireId = Number(id);

  const query = useQuery({
    queryKey: ['sales-order', spireId],
    queryFn: ({ signal }) => api.salesOrders.getById(spireId, signal),
    enabled: Number.isFinite(spireId),
  });

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/operations"
          aria-label="Back to operations"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>Operations</span>
        </Link>
      </div>

      {query.isError && (
        <QueryError
          title={query.error.kind === 'not_found' ? 'Order not found' : "Couldn't load order"}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.isPending && <Skeleton />}
      {query.data && <Body order={query.data} />}
    </div>
  );
}

function Body({ order }: { order: SalesOrder }) {
  const types = useSalesTypes();
  const trade = orderTrade(types, order, useFieldJobsSettings().typeUdfKey);
  return (
    <>
      <div className="space-y-2">
        <div className="font-mono text-xs text-ink-muted">{order.orderNo}</div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {order.customer.spireId !== null ? (
            <Link
              to="/customers/$id"
              params={{ id: String(order.customer.spireId) }}
              className="inline-flex items-center gap-1.5 hover:underline"
              title="Open customer"
            >
              {order.customer.name}
              <ExternalLink className="h-4 w-4 text-ink-subtle" aria-hidden />
            </Link>
          ) : (
            // Cash sale / unlinked order — nothing to open (see spire-client toCustomerRef).
            order.customer.name
          )}
        </h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusPill status={order.status} />
          <TypePill type={order.type} />
          {order.hold && <Pill variant="danger">On hold</Pill>}
          <TradePill types={types} trade={trade} hideUnknown />
          {order.phaseId && (
            <Pill variant="neutral" title="Spire order phase">
              <span className="font-mono">{order.phaseId}</span>
            </Pill>
          )}
        </div>
        <div className="text-xs text-ink-muted">
          {order.orderDate ?? '—'} · <span className="font-mono">{order.customer.customerNo}</span> · {order.currencyCode}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Bill to</CardTitle></CardHeader>
          <CardBody className="text-sm text-ink space-y-0.5">
            <AddressBlock address={order.billingAddress} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader><CardTitle>Ship to</CardTitle></CardHeader>
          <CardBody className="text-sm text-ink space-y-0.5">
            <AddressBlock address={order.shippingAddress} />
          </CardBody>
        </Card>
      </div>

      <DispatchCard order={order} />
      {order.status === 'open' && <ScheduleCard order={order} />}

      <Card>
        <CardHeader><CardTitle>Lines</CardTitle></CardHeader>
        <CardBody>
          {order.items.length === 0 ? (
            <div className="text-sm text-ink-muted">No lines on this order.</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              {order.items.map((line) => (
                <LineRow key={line.spireId} line={line} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Totals</CardTitle></CardHeader>
        <CardBody className="text-sm space-y-1.5">
          <Row label="Subtotal" value={`$${order.subtotal}`} />
          {order.totalDiscount !== '0' && (
            <Row label="Discount" value={`−$${order.totalDiscount}`} />
          )}
          {order.freight !== '0' && <Row label="Freight" value={`$${order.freight}`} />}
          {order.taxes
            .filter((t) => t.total !== '0')
            .map((t, idx) => (
              <TaxRow key={`${t.code ?? 'tax'}-${idx}`} tax={t} />
            ))}
          <div className="pt-2 mt-2 border-t" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            <Row label="Total" value={`$${order.total}`} bold />
          </div>
        </CardBody>
      </Card>

      <TimeOnJobCard spireId={order.spireId} />

      <Card>
        <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
        <CardBody>
          <JobNotes
            spireId={order.spireId}
            canAdd
            emptyHint="No notes yet. Add one above for the tech; sales reps add theirs on the quote, techs on site."
          />
        </CardBody>
      </Card>

      <PhotosCard spireId={order.spireId} />
    </>
  );
}

function AddressBlock({ address }: { address: SalesOrder['billingAddress'] }) {
  return (
    <>
      {address.name && <div className="font-medium">{address.name}</div>}
      <div>{address.line1 ?? '—'}</div>
      {address.line2 && <div>{address.line2}</div>}
      <div>
        {[address.city, address.provState, address.postalCode].filter(Boolean).join(' ')}
      </div>
      <div>{address.country ?? ''}</div>
    </>
  );
}

function LineRow({ line }: { line: SalesOrderLineItem }) {
  const isComment = !line.inventory && line.partNo === '';
  return (
    <li className="py-3 flex items-start gap-3 text-sm">
      <div className="min-w-0 flex-1">
        {!isComment && (
          <div className="font-mono text-[11px] text-ink-subtle">
            #{line.sequence} {line.partNo}
          </div>
        )}
        <div className={isComment ? 'text-ink-muted italic' : 'text-ink'}>
          {line.description ?? line.comment ?? '(no description)'}
        </div>
      </div>
      {!isComment && (
        <div className="text-right shrink-0">
          <div className="text-xs text-ink-muted">
            {line.committedQty} {line.sellMeasure ?? ''}{' '}
            <span className="text-ink-subtle">×</span> ${line.unitPrice}
          </div>
          <div className="font-medium text-ink tabular-nums">${line.extendedPrice ?? line.extendedPriceCommitted}</div>
        </div>
      )}
    </li>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? 'text-base font-semibold text-ink' : ''}`}>
      <span className={bold ? '' : 'text-ink-muted'}>{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

function StatusPill({ status }: { status: SalesOrder['status'] }) {
  if (status === 'open') return <Pill variant="success" dot>Open</Pill>;
  if (status === 'invoiced') return <Pill variant="neutral">Invoiced</Pill>;
  if (status === 'cancelled') return <Pill variant="danger">Cancelled</Pill>;
  return <Pill variant="neutral">{status}</Pill>;
}

function TypePill({ type }: { type: SalesOrder['type'] }) {
  if (type === 'quote') return <Pill variant="warning">Quote</Pill>;
  if (type === 'order') return <Pill variant="info">Work order</Pill>;
  return <Pill variant="neutral">{type}</Pill>;
}

function TaxRow({ tax }: { tax: SalesOrderTax }) {
  return (
    <Row
      label={`${tax.name ?? 'Tax'} (${tax.rate}%)`}
      value={`$${tax.total}`}
    />
  );
}

/**
 * The UDF key Spire customers conventionally use for trade categorization.
 * Verified in spirelive: order udf objects include `SALES TYP` with values
 * like "Install" / "Service". If a deployment uses a different key, this
 * constant is the one place to change it.
 */
const TRADE_UDF_KEY = 'SALES TYP';

function ScheduleCard({ order }: { order: SalesOrder }) {
  const queryClient = useQueryClient();
  const [requiredDate, setRequiredDate] = useState(order.requiredDate ?? '');
  const [shipDate, setShipDate] = useState(order.shipDate ?? '');
  const [hold, setHold] = useState(order.hold);
  const [trade, setTrade] = useState(order.udf[TRADE_UDF_KEY] ?? '');
  const [feedback, setFeedback] = useState<string | null>(null);
  // Dropdown = the active sales types' labels (what the office writes into
  // SALES TYP), plus the order's current value if it's something else so
  // it isn't silently replaced on save.
  const salesTypes = useSalesTypes();
  const currentValue = order.udf[TRADE_UDF_KEY] ?? '';
  const tradeOptions = [
    '',
    ...activeSalesTypes(salesTypes).map((t) => t.label),
    ...(currentValue && !activeSalesTypes(salesTypes).some((t) => t.label === currentValue) ? [currentValue] : []),
  ];

  // Re-sync local form state when the order refetches (e.g. after save).
  useEffect(() => {
    setRequiredDate(order.requiredDate ?? '');
    setShipDate(order.shipDate ?? '');
    setHold(order.hold);
    setTrade(order.udf[TRADE_UDF_KEY] ?? '');
  }, [order.requiredDate, order.shipDate, order.hold, order.udf]);

  const mutation = useMutation<SalesOrder, ApiError, UpdateSalesOrderBody>({
    mutationFn: (patch) => api.salesOrders.update(order.spireId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(['sales-order', order.spireId], updated);
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['dispatch'] });
      setFeedback('Saved.');
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (err) => {
      setFeedback(`Failed: ${err.message}`);
    },
  });

  const currentTrade = order.udf[TRADE_UDF_KEY] ?? '';
  const dirty =
    requiredDate !== (order.requiredDate ?? '') ||
    shipDate !== (order.shipDate ?? '') ||
    hold !== order.hold ||
    trade !== currentTrade;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const patch: UpdateSalesOrderBody = {};
    if (requiredDate !== (order.requiredDate ?? '')) {
      patch.requiredDate = requiredDate || undefined;
    }
    if (shipDate !== (order.shipDate ?? '')) {
      patch.shipDate = shipDate || undefined;
    }
    if (hold !== order.hold) patch.hold = hold;
    if (trade !== currentTrade) {
      patch.udf = { [TRADE_UDF_KEY]: trade };
    }
    if (Object.keys(patch).length === 0) return;
    mutation.mutate(patch);
  };

  return (
    <Card>
      <CardHeader><CardTitle>Schedule</CardTitle></CardHeader>
      <CardBody>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Required by</span>
              <Input
                type="date"
                value={requiredDate}
                onChange={(e) => setRequiredDate(e.target.value)}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Ship date</span>
              <Input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
              />
            </label>
          </div>
          <label className="space-y-1 text-sm block">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Trade ({TRADE_UDF_KEY})</span>
            <select
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              className="h-11 w-full rounded-lg border bg-surface px-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand"
              style={{ borderColor: 'var(--color-border-secondary)' }}
            >
              {tradeOptions.map((t) => (
                <option key={t || 'none'} value={t}>
                  {t || '— none —'}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hold}
              onChange={(e) => setHold(e.target.checked)}
              className="h-4 w-4 accent-trade-emergency"
            />
            Put order on hold
          </label>
          <div className="flex items-center gap-3 justify-end">
            {feedback && (
              <span
                className={
                  feedback.startsWith('Failed') ? 'text-xs text-red-700' : 'text-xs text-emerald-700'
                }
              >
                {feedback}
              </span>
            )}
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              disabled={!dirty || mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save schedule'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function TimeOnJobCard({ spireId }: { spireId: number }) {
  const query = useQuery({
    queryKey: ['sales-orders', spireId, 'time-entries'],
    queryFn: ({ signal }) => api.field.entriesForOrder(spireId, signal),
    staleTime: 30_000,
  });
  const entries = query.data ?? [];
  if (query.isPending) {
    return (
      <Card>
        <CardHeader><CardTitle>Time on job</CardTitle></CardHeader>
        <CardBody className="text-sm text-ink-muted">Loading…</CardBody>
      </Card>
    );
  }
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Time on job</CardTitle></CardHeader>
        <CardBody className="text-sm text-ink-muted">
          No time tracked yet. Field tech entries appear here as the visit progresses.
        </CardBody>
      </Card>
    );
  }
  // Aggregate by phase. Closed entries have a server-stamped durationSec;
  // the open one (endedAt=null) is computed client-side against now so the
  // office sees a live total even mid-visit.
  const now = Date.now();
  const byPhase = new Map<string, { sec: number; segments: number }>();
  let total = 0;
  for (const e of entries) {
    let sec = e.durationSec;
    if (sec === null) {
      const start = Date.parse(e.startedAt);
      sec = Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 1000)) : 0;
    }
    total += sec;
    const cur = byPhase.get(e.phase) ?? { sec: 0, segments: 0 };
    cur.sec += sec;
    cur.segments += 1;
    byPhase.set(e.phase, cur);
  }
  const phaseOrder = ['traveling', 'on_site', 'work_in_progress', 'paused', 'work_completed', 'signed_off'];
  const rows = phaseOrder
    .filter((p) => byPhase.has(p))
    .map((p) => ({ phase: p, ...byPhase.get(p)! }));
  return (
    <Card>
      <CardHeader><CardTitle>Time on job</CardTitle></CardHeader>
      <CardBody className="text-sm">
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.phase} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-ink">{phaseLabelLocal(r.phase)}</span>
                {r.segments > 1 && (
                  <span className="text-[10.5px] text-ink-subtle">
                    ({r.segments} segments)
                  </span>
                )}
              </div>
              <span className="font-mono text-ink tabular-nums">{formatHMS(r.sec)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 pt-2 border-t flex items-center justify-between font-medium" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <span>Total</span>
          <span className="font-mono tabular-nums">{formatHMS(total)}</span>
        </div>
      </CardBody>
    </Card>
  );
}

function phaseLabelLocal(phase: string): string {
  switch (phase) {
    case 'traveling': return 'Traveling';
    case 'on_site': return 'On site';
    case 'work_in_progress': return 'Work in progress';
    case 'paused': return 'Paused';
    case 'work_completed': return 'Work completed';
    case 'signed_off': return 'Signed off';
    case 'scheduled': return 'Scheduled';
    default: return phase;
  }
}

function formatHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h)}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function PhotosCard({ spireId }: { spireId: number }) {
  return (
    <Card>
      <CardHeader><CardTitle>Job files</CardTitle></CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-ink-muted">
          Photos and PDFs attached here show on the installer's job screen. Photos are also
          copied to the order's CRM notes in Spire.
        </p>
        <JobFiles spireId={spireId} />
      </CardBody>
    </Card>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <div className="h-4 w-24 bg-surface-muted rounded animate-pulse" />
      <div className="h-7 w-1/2 bg-surface-muted rounded animate-pulse" />
      <Card>
        <CardBody className="space-y-2">
          <div className="h-3 w-1/3 bg-surface-muted rounded animate-pulse" />
          <div className="h-3 w-1/2 bg-surface-muted rounded animate-pulse" />
        </CardBody>
      </Card>
    </div>
  );
}
